// components/Signatories/SignatoriesModal.js
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAccountSignatories } from '../../service/Hooks/useAccountSignatories';
import { useLoader } from '../../service/Hooks/useLoader';
import Select from 'react-select';
import SignatoryDetailModal from './SignatoryDetailModal';
import ConfirmationModal from '../../common/ConfirmationModal';
import Pagination from '../../LegalRepresentatives/Pagination';

// Composant de statistiques
// Composant de statistiques robuste avec gestion d'erreurs
const SignatoryStats = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="row g-3 mb-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="col-md-3">
            <div className="card border-0 bg-light">
              <div className="card-body text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <small className="d-block mt-2 text-muted">Chargement...</small>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="alert alert-warning text-center">
        <i className="fas fa-exclamation-triangle me-2"></i>
        Statistiques non disponibles
      </div>
    );
  }

  // Fonction pour obtenir le nombre par type de signature
  const getTypeSignatureCount = (type) => {
    return stats.parTypeSignature?.[type] || 0;
  };

  return (
    <div className="row g-3 mb-4">
      <div className="col-md-3">
        <div className="card bg-success bg-opacity-10  border border-success border-opacity-25 text-success">
          <div className="card-body text-center py-3">
            <h4 className="mb-1">{stats.totalSignataires || 0}</h4>
            <small className="text text-success fs-5 fw-bold">Total Signataires</small>
          </div>
        </div>
      </div>
      <div className="col-md-3">
        <div className="card bg-warning bg-opacity-10  border border-warning border-opacity-25 text-warning">
          <div className="card-body text-center py-3">
            <h4 className="mb-1">{stats.signatairesPrincipaux || 0}</h4>
            <small className="text text-warning fs-5 fw-bold">Principaux</small>
          </div>
        </div>
      </div>
      <div className="col-md-3">
        <div className="card bg-dark bg-opacity-10  border border-dark border-opacity-25 text-dark">
          <div className="card-body text-center py-3">
            <h4 className="mb-1">{stats.representantsLegaux || 0}</h4>
            <small className="text text-dark fs-5 fw-bold">Représentants Légaux</small>
          </div>
        </div>
      </div>
      <div className="col-md-3">
        <div className="card bg-danger bg-opacity-10  border border-danger border-opacity-25 text-danger">
          <div className="card-body text-center py-3">
            <h4 className="mb-1">{getTypeSignatureCount('Seul')}</h4>
            <small className="text text-danger fs-5 fw-bold">Signature Seule</small>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant de filtres pour signataires
const SignatoryFilterBar = ({ filters, onFiltersChange, loading }) => {
  const sortOptions = [
    { value: 'CreationDate', label: 'Date de création' },
    { value: 'firstName', label: 'Prénom' },
    { value: 'lastName', label: 'Nom' },
    { value: 'position', label: 'Poste' },
    { value: 'typeSignature', label: 'Type signature' }
  ];

  const typeSignatureOptions = [
    { value: null, label: 'Tous les types' },
    { value: 'Seul', label: 'Seul' },
    { value: 'Conjointe', label: 'Conjointe' },
    { value: 'Multiple', label: 'Multiple' }
  ];

  const booleanOptions = [
    { value: null, label: 'Tous' },
    { value: true, label: 'Oui' },
    { value: false, label: 'Non' }
  ];

  const pageSizeOptions = [
    { value: 5, label: '5 éléments' },
    { value: 10, label: '10 éléments' },
    { value: 25, label: '25 éléments' },
    { value: 50, label: '50 éléments' }
  ];

  const handleSearchChange = (e) => {
    onFiltersChange({
      ...filters,
      searchTerm: e.target.value,
      pageNumber: 1
    });
  };

  const handleFilterChange = (field, value) => {
    onFiltersChange({
      ...filters,
      [field]: value,
      pageNumber: 1
    });
  };

  return (
    <div className="row g-3 mb-4">
      {/* Barre de recherche */}
      <div className="col-md-3">
        <label className="form-label small fw-semibold text-muted">
          Rechercher
        </label>
        <input
          type="text"
          className="search-input"
          placeholder="Nom, prénom, poste, email..."
          value={filters.searchTerm}
          onChange={handleSearchChange}
          disabled={loading}
        />
      </div>

      {/* Type de signature */}
      <div className="col-md-2">
        <label className="form-label small fw-semibold text-muted">
          Type signature
        </label>
        <Select
          options={typeSignatureOptions}
          value={typeSignatureOptions.find(opt => opt.value === filters.typeSignature)}
          onChange={(option) => handleFilterChange('typeSignature', option.value)}
          isDisabled={loading}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>

      {/* Signataire principal */}
      <div className="col-md-2">
        <label className="form-label small fw-semibold text-muted">
          Principal
        </label>
        <Select
          options={booleanOptions}
          value={booleanOptions.find(opt => opt.value === filters.estSignatairePrincipal)}
          onChange={(option) => handleFilterChange('estSignatairePrincipal', option.value)}
          isDisabled={loading}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>

      {/* Représentant légal */}
      <div className="col-md-2">
        <label className="form-label small fw-semibold text-muted">
          Rep. Légal
        </label>
        <Select
          options={booleanOptions}
          value={booleanOptions.find(opt => opt.value === filters.estRepresentantLegal)}
          onChange={(option) => handleFilterChange('estRepresentantLegal', option.value)}
          isDisabled={loading}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>

      {/* Taille de page */}
      <div className="col-md-2">
        <label className="form-label small fw-semibold text-muted">
          Par page
        </label>
        <Select
          options={pageSizeOptions}
          value={pageSizeOptions.find(opt => opt.value === filters.pageSize)}
          onChange={(option) => handleFilterChange('pageSize', option.value)}
          isDisabled={loading}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>
    </div>
  );
};

// Composant principal SignatoriesModal
const SignatoriesModal = ({ account = {}, accountId, onClose, onShowRepresentatives }) => {
  const {
    signatories,
    stats,
    pagination,
    loading,
    error,
    filters,
    setFilters,
    deleteSignatory
  } = useAccountSignatories(accountId);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [signatoryToDelete, setSignatoryToDelete] = useState(null);
  const [detailModal, setDetailModal] = useState({
    isOpen: false,
    signatory: null
  });

  const { showLoader, hideLoader } = useLoader();

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, pageNumber: newPage }));
  };

  const handleDeleteClick = (signatory) => {
    setSignatoryToDelete(signatory);
    setShowConfirmationModal(true);
  };

  const handleViewDetails = (signatory) => {
    setDetailModal({
      isOpen: true,
      signatory
    });
  };

  const confirmDelete = async () => {
    showLoader({ message: "Suppression en cours...", indeterminate: true });
    if (signatoryToDelete) {
      const success = await deleteSignatory(signatoryToDelete.id);
      if (success) {
        setShowConfirmationModal(false);
        setSignatoryToDelete(null);
      }
      hideLoader();
    }
  };

  const cancelDelete = () => {
    setShowConfirmationModal(false);
    setSignatoryToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadgeClass = (statut) => {
    switch (statut?.toLowerCase()) {
      case 'actif':
        return 'bg-success bg-opacity-10 text-success border border-success border-opacity-25';
      case 'inactif':
        return 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25';
      default:
        return 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25';
    }
  };

  const getTypeSignatureBadgeClass = (type) => {
    switch (type) {
      case 'Seul':
        return 'bg-primary bg-opacity-10 text-darck border border-primary border-opacity-25';
      case 'Conjointe':
        return 'bg-warning text-warning bg-opacity-10 border border-warning border-opacity-25';
      case 'Multiple':
        return 'bg-info bg-opacity-10 text-info border border-info border-opacity-25';
      default:
        return 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25';
    }
  };

  const title = (
    <>
      Signataires -{" "}
      <strong className="text-primary">
        {account?.companyName || "Entreprise"}
      </strong>
      {pagination && (
        <span className="badge bg-light text-success ms-2">
          {pagination.totalCount}
        </span>
      )}
    </>
  );

  return (
    <>
      {ReactDOM.createPortal(
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable modal-custom">
            <div className="modal-content">
              {/* En-tête */}
              <div className="modal-header bg-white text-white">
                <div className="d-flex align-items-center w-100 justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-signature me-2 text-primary"></i>
                    <h5 className="modal-title mb-0">{title}</h5>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn-close btn-close-primary border-1"
                      onClick={onClose}
                      disabled={loading}
                    ></button>
                  </div>
                </div>
              </div>

              <div className="modal-body">
                {error && !loading && (
                  <div className="alert alert-danger text-center">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                  </div>
                )}

                {/* Statistiques */}
                <SignatoryStats stats={stats} loading={loading} />

                {/* Filtres */}
                <SignatoryFilterBar
                  filters={filters}
                  onFiltersChange={setFilters}
                  loading={loading}
                />

                {/* Contenu */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-success" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-2 text-muted">Chargement des signataires...</p>
                  </div>
                ) : signatories.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover table-striped align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Nom Complet</th>
                            <th>Poste</th>
                            <th>Email</th>
                            <th>Type Signature</th>
                            <th>Principal</th>
                            <th>Statut</th>
                            <th>Date Création</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {signatories.map((signatory) => (
                            <tr key={signatory.id}>
                              <td>
                                <div className="fw-semibold">
                                  {signatory.firstName} {signatory.lastName}
                                </div>
                              </td>
                              <td>{signatory.position}</td>
                              <td>
                                <a
                                  href={`mailto:${signatory.email}`}
                                  className="text-decoration-none"
                                >
                                  {signatory.email}
                                </a>
                              </td>
                              <td>
                                <span className={`badge ${getTypeSignatureBadgeClass(signatory.typeSignature)}`}>
                                  {signatory.typeSignature}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${signatory.estSignatairePrincipal ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25' : 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25'}`}>
                                  {signatory.estSignatairePrincipal ? 'Oui' : 'Non'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${getStatusBadgeClass(signatory.statut)}`}>
                                  {signatory.statut}
                                </span>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {formatDate(signatory.creationDate)}
                                </small>
                              </td>
                              <td className="text-center">
                                <div className="btn-group btn-group-sm" role="group">
                                  <button
                                    className="btn btn-outline-primary"
                                    onClick={() => handleViewDetails(signatory)}
                                    title="Voir les détails"
                                  >
                                    <i className="fas fa-eye"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => handleDeleteClick(signatory)}
                                    title="Supprimer"
                                    disabled={signatory.isDeleted}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {pagination && pagination.totalPages > 1 && (
                      <div className="mt-4">
                        <Pagination
                          pagination={pagination}
                          onPageChange={handlePageChange}
                          loading={loading}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-5">
                    <i className="fas fa-signature fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">Aucun signataire trouvé</h5>
                    <p className="text-muted">
                      Aucun signataire ne correspond à vos critères de recherche.
                    </p>
                  </div>
                )}
              </div>

              {/* Pied de page */}
              <div className="modal-footer">
                <div className="w-100 d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Compte: <strong>{account?.companyName}</strong>
                  </small>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                  >
                    <i className="fas fa-times me-1"></i>
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Supprimer le signataire"
        message={`Êtes-vous sûr de vouloir supprimer le signataire "${signatoryToDelete?.firstName} ${signatoryToDelete?.lastName}" ?`}
        confirmText="Supprimer"
        type="danger"
        loading={loading}
      />

      {/* Modal de détails signataire */}
      <SignatoryDetailModal
        isOpen={detailModal.isOpen}
        onClose={() => setDetailModal({ isOpen: false, signatory: null })}
        signatory={detailModal.signatory}
      />
    </>
  );
};

export default SignatoriesModal;